import { useEffect } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { MenuPopover } from 'src/components';
import { Badge, Button, List, Typography, useTheme } from '@mui/material';
import { FilterIcon } from 'src/assets/icons/FilterIcon';
import {
  FILTERS_BTN_TEXT,
  FILTER_POPOVER_APPLY_BTN,
  FILTER_POPOVER_CLEAR_ALL_BTN,
  FILTER_POPOVER_TITLE,
  filters,
} from './mapFilterConfig';
import { FilterItem } from './FilterItem';
import {
  FilterButtonText,
  FilterFormButtons,
  filterButton,
  filterButtonInside,
  filterList,
  filterMenuPopover,
  filterPopoverTitle,
} from './style';
import { SiteType } from '../../types/types';
import { useMapFilters } from 'src/contexts/siteMapFilterContext';
import { useHtmlElement } from 'src/hooks/useHtmlElement';

type Inputs = {
  [key in keyof typeof SiteType]: boolean;
};

const initialValues: Inputs = {
  lift_station: false,
  water_treatment_plant: false,
  wastewater_treatment_plant: false,
  booster_pump_station: false,
  water_tower: false,
  process_plant: false,
  combined_sewage_overflow: false,
  water_well: false,
};

const getFilterDefaultValues = (siteTypes: string) => {
  if (siteTypes) {
    const selectedSiteTypes = siteTypes.split(',');
    const values: { [key: string]: boolean } = {};

    for (const key in initialValues) {
      if (selectedSiteTypes.includes(key)) {
        values[key] = true;
      } else {
        values[key] = false;
      }
    }

    return values;
  }

  return initialValues;
};

export const MapFilter = ({ insideInput = false }: { insideInput?: boolean }) => {
  const theme = useTheme();
  const { htmlElement, removeHtmlElement, addHtmlElement } = useHtmlElement();
  const { updateMapFilters, mapFilters } = useMapFilters();
  const defaultValues = getFilterDefaultValues(mapFilters);
  const methods = useForm<Inputs>({ defaultValues });
  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<Inputs> = (data: any) => {
    const selectedSiteTypes = Object.entries(data)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    updateMapFilters(selectedSiteTypes.toString());
    removeHtmlElement();
  };

  const onResetFilters = () => {
    reset(initialValues);
  };

  // Update default values whenever mapFilters change using filters track
  useEffect(() => {
    const defaultValues = getFilterDefaultValues(mapFilters);
    methods.reset(defaultValues);
  }, [mapFilters, methods]);

  return (
    <>
      <Badge
        color="error"
        variant="dot"
        overlap="circular"
        invisible={!mapFilters}
        data-testid="map-filter-badge"
      >
        <Button
          onClick={(event) => addHtmlElement(event.currentTarget)}
          variant={htmlElement || mapFilters ? 'contained' : 'text'}
          endIcon={<FilterIcon fill={insideInput ? theme.palette.primary.main : undefined} />}
          sx={insideInput ? { ...filterButton, ...filterButtonInside(theme) } : filterButton}
          data-testid="map-filter-button"
        >
          <FilterButtonText>{FILTERS_BTN_TEXT}</FilterButtonText>
        </Button>
      </Badge>
      <MenuPopover
        open={htmlElement}
        onClose={() => {
          removeHtmlElement();
        }}
        disabledArrow
        sx={filterMenuPopover}
      >
        <List data-testid="map-filter-list" sx={filterList}>
          <Typography variant="h6" sx={filterPopoverTitle}>
            {FILTER_POPOVER_TITLE}
          </Typography>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {filters.map((item) => (
                <FilterItem key={item.id} item={item} />
              ))}
              <FilterFormButtons>
                <Button
                  data-testid="map-filter-clear-all-btn"
                  variant="outlined"
                  onClick={onResetFilters}
                >
                  {FILTER_POPOVER_CLEAR_ALL_BTN}
                </Button>
                <Button variant="contained" type="submit">
                  {FILTER_POPOVER_APPLY_BTN}
                </Button>
              </FilterFormButtons>
            </form>
          </FormProvider>
        </List>
      </MenuPopover>
    </>
  );
};
